import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Container, Text, Link, Heading, Picture } from '../../components/index.js'
import { cn, atobID, getLocalizedPath } from '../../helpers/utils.js'
import { DownArrow } from './icons/index.js'
import { useNavContext } from './NavProvider.js'
import type { Product, ProductVariant } from '../../cpn-components/CpnProductCard/types.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

/**
 * 侧边栏下拉
 * @param categoriesItem 分类项
 */
export const SidebarDropdown = React.memo(
  ({ sidebarCategoriesMetadata, seriesMetadata }: { sidebarCategoriesMetadata: any; seriesMetadata: any }) => {
    const { buildProps, onSidebarNavClick } = useNavContext()
    const { locale = 'us' } = useAiuiContext()
    const [expandedSubcategory, setExpandedSubcategory] = useState<{ index: number; open: boolean }[]>([])
    const [activeSubSubcategoryIndex, setActiveSubSubcategoryIndex] = useState(-1)
    const sidebarDropdownRef = useRef<HTMLDivElement>(null)
    const lastClickRef = useRef<{ index: number; hasSubSubCategories: boolean } | null>(null)

    const initExpandedSubcategory = useCallback(() => {
      const subcategories = sidebarCategoriesMetadata?.subcategories
      if (!subcategories?.length) return

      const withSubSubCategoriesIndex = subcategories.findIndex((item: any) => !!item?.subSubCategories)
      const withoutSubSubCategoriesIndex = subcategories.findIndex((item: any) => !item?.subSubCategories)

      // 创建展开状态数组
      const expandedStates = subcategories.map((_: any, index: number) => ({
        index,
        open: withSubSubCategoriesIndex === index || withoutSubSubCategoriesIndex === index,
      }))

      setExpandedSubcategory(expandedStates)
    }, [sidebarCategoriesMetadata])

    useEffect(() => {
      initExpandedSubcategory()
    }, [initExpandedSubcategory])

    // 通过categoriesMetadata的subSubCategories的label与series的label匹配
    const matchSeriesMetadata = useMemo(() => {
      const subCategory =
        sidebarCategoriesMetadata?.subcategories?.[expandedSubcategory?.find(item => item.open)?.index || 0]
      const currentSeriesMetadata =
        seriesMetadata?.find((item: any) => item?.label?.toLowerCase() === subCategory?.label?.toLowerCase()) || {}
      if (subCategory?.collections) {
        const category = buildProps?.categories?.[subCategory?.collections] || {}
        return {
          label: currentSeriesMetadata?.label,
          isCollection: true,
          banner: currentSeriesMetadata?.banner,
          primary: currentSeriesMetadata?.primary,
          series: [
            {
              products: category?.products,
            },
          ],
        }
      } else {
        if (subCategory?.subSubCategories) {
          const activeSubSubcategory = subCategory?.subSubCategories?.[activeSubSubcategoryIndex]
          const currentActiveSeriesMetadata =
            seriesMetadata?.find(
              (item: any) => item?.label?.toLowerCase() === activeSubSubcategory?.label?.toLowerCase()
            ) || {}
          if (activeSubSubcategory?.collections) {
            const category = buildProps?.categories?.[activeSubSubcategory?.collections] || {}
            return {
              label: currentActiveSeriesMetadata?.label,
              isCollection: true,
              banner: currentActiveSeriesMetadata?.banner,
              primary: currentActiveSeriesMetadata?.primary,
              series: [
                {
                  products: category?.products,
                },
              ],
            }
          } else {
            return currentActiveSeriesMetadata
          }
        } else {
          return currentSeriesMetadata
        }
      }
    }, [sidebarCategoriesMetadata, expandedSubcategory, activeSubSubcategoryIndex, seriesMetadata, buildProps])

    const handleSubcategoryOpen = useCallback(
      (index: number, subcategoryItem: any) => {
        const hasSubSubCategories = subcategoryItem?.subSubCategories?.length > 0
        hasSubSubCategories ? setActiveSubSubcategoryIndex(0) : setActiveSubSubcategoryIndex(-1)

        const lastClick = lastClickRef.current

        setExpandedSubcategory(prev =>
          prev.map((item, i) => {
            if (i === index) {
              return { ...item, open: !item.open }
            }
            // 如果当前点击的项有子子分类，关闭其他所有项
            if (hasSubSubCategories) {
              return { ...item, open: false }
            }
            if (!lastClick && activeSubSubcategoryIndex === -1) {
              return { ...item, open: false }
            }
            if (
              lastClick &&
              !lastClick.hasSubSubCategories &&
              !hasSubSubCategories &&
              activeSubSubcategoryIndex === -1
            ) {
              // 如果上一次点击的项没有子子分类，且这次点击的也没有子子分类，并且没有点击上一次没有subSubCategories的item 关闭上一次的项
              return { ...item, open: false }
            }
            return item
          })
        )

        // 更新上一次点击的状态
        lastClickRef.current = { index, hasSubSubCategories }
      },
      [activeSubSubcategoryIndex]
    )

    const handleSubSubcategoryOpen = (index: number, subSubindex: number) => {
      setExpandedSubcategory(prev =>
        prev.map(item => (item.index === index ? { ...item, open: true } : { ...item, open: false }))
      )
      setActiveSubSubcategoryIndex(subSubindex)
    }

    return (
      <Container childClassName="lg-desktop:gap-12 flex bg-white gap-8">
        <div
          className="lg-desktop:basis-[356px] flex basis-[284px] flex-col gap-4 py-4"
          style={{
            userSelect: 'none',
          }}
          ref={sidebarDropdownRef}
        >
          <div
            className="desktop:h-[416px] flex flex-col overflow-y-auto"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {sidebarCategoriesMetadata?.subcategories?.map((subItem: any, index: number) => {
              const hasSubSubCategory =
                Array.isArray(subItem?.subSubCategories) && subItem?.subSubCategories?.length > 0
              const isExpanded = expandedSubcategory?.find(item => item.index === index)?.open
              return (
                <div key={`${subItem?.label || ''}subcategoryItem-${index}`}>
                  <button
                    className={cn(
                      'rounded-sidebar-shelf flex w-full cursor-pointer items-center justify-between border-0 bg-transparent text-left',
                      {
                        'bg-[#F5F5F7]': !hasSubSubCategory && isExpanded,
                      }
                    )}
                    onClick={() => {
                      handleSubcategoryOpen(index, subItem)
                      onSidebarNavClick?.(subItem, index)
                    }}
                    aria-expanded={hasSubSubCategory ? isExpanded : undefined}
                    aria-haspopup={hasSubSubCategory ? 'true' : undefined}
                  >
                    <Text html={subItem.label} className="p-4 text-sm font-bold leading-[1.4]" />
                    {hasSubSubCategory && (
                      <DownArrow
                        aria-hidden="true"
                        className={cn('size-4', {
                          ['rotate-180']: isExpanded,
                        })}
                      />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col" role="menu">
                      {subItem.subSubCategories?.map((subSubItem: any, subSubindex: number) => (
                        <button
                          onClick={() => {
                            handleSubSubcategoryOpen(index, subSubindex)
                            onSidebarNavClick?.(subSubItem, subSubindex)
                          }}
                          key={`subSubItem-${index}-${subSubindex}`}
                          className={cn(
                            'rounded-sidebar-shelf w-full cursor-pointer border-0 bg-transparent px-6 py-4 text-left leading-none hover:bg-[#F5F5F7]',
                            {
                              'bg-[#F5F5F7]': activeSubSubcategoryIndex === subSubindex,
                            }
                          )}
                          role="menuitem"
                          aria-label={subSubItem.label}
                        >
                          <Text html={subSubItem.label} className="text-sm font-bold leading-[1.4] text-[#4A4C56]" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {sidebarCategoriesMetadata && (
            <div className="flex">
              <div className="flex flex-col gap-4">
                {sidebarCategoriesMetadata?.primary && (
                  <Button
                    as="a"
                    href={`${getLocalizedPath(sidebarCategoriesMetadata?.primary?.url, locale)}?ref=${sidebarCategoriesMetadata?.primary?.label}_shopall`}
                    variant="primary"
                    size="lg"
                    className="lg-desktop:text-base text-sm"
                  >
                    {sidebarCategoriesMetadata?.primary?.label}
                  </Button>
                )}
                {sidebarCategoriesMetadata?.secondary && (
                  <Button
                    as="a"
                    href={sidebarCategoriesMetadata?.secondary?.url}
                    variant="link"
                    size="lg"
                    iconClassName="size-4"
                    className="lg-desktop:text-base justify-start !p-0 text-sm"
                  >
                    {sidebarCategoriesMetadata?.secondary?.label}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 py-4">
          {Reflect.ownKeys(matchSeriesMetadata).length > 0 && (
            <div className="mb-4 flex items-center justify-between">
              {(() => {
                const labelContent = (
                  <>
                    <Text
                      html={matchSeriesMetadata?.label}
                      className="lg-desktop:text-2xl text-xl font-bold leading-[1.4]"
                    />
                    {matchSeriesMetadata?.primary && !matchSeriesMetadata?.primary?.hide && (
                      <Text
                        className="lg-desktop:text-base text-sm font-bold leading-[1.2]"
                        html={matchSeriesMetadata?.primary?.label}
                      />
                    )}
                  </>
                )

                return matchSeriesMetadata?.primary?.url ? (
                  <Button
                    as="a"
                    href={`${getLocalizedPath(matchSeriesMetadata?.primary?.url, locale)}?ref=${matchSeriesMetadata?.label}_viewmore`}
                    variant="link"
                    size="lg"
                    iconClassName="size-4"
                    className="flex items-center justify-start gap-2 !p-0 no-underline"
                  >
                    {labelContent}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">{labelContent}</div>
                )
              })()}
              {sidebarCategoriesMetadata?.guide?.label && (
                <Link
                  href={getLocalizedPath(sidebarCategoriesMetadata?.guide?.url, locale)}
                  className="lg-desktop:text-base text-sm leading-[1.2] text-[#6D6D6F]"
                >
                  {sidebarCategoriesMetadata?.guide?.label}
                </Link>
              )}
            </div>
          )}
          <div
            className="flex h-[426px] flex-col gap-4 overflow-y-auto"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {matchSeriesMetadata?.series?.map((seriesItem: any, seriesIndex: number) => (
              <div key={`seriesItem-${seriesItem?.label || ''}-${seriesIndex}`}>
                {seriesItem.label && (
                  <Text
                    html={seriesItem.label}
                    as="p"
                    className="mb-2 text-sm font-bold leading-[1.4] text-[#6D6D6F]"
                  />
                )}
                <div className="grid grid-cols-3 gap-4">
                  {!!matchSeriesMetadata?.banner && (
                    <Link
                      asChild={!matchSeriesMetadata?.banner?.href}
                      href={getLocalizedPath(matchSeriesMetadata?.banner?.href, locale)}
                    >
                      <div className="relative [&_img]:hover:scale-110 [&_img]:hover:transition-all [&_img]:hover:duration-500">
                        <Picture
                          source={matchSeriesMetadata?.banner?.imageUrl}
                          className="laptop:h-[120px] h-[114px]"
                          imgClassName="object-cover h-full"
                        />
                        <div className="absolute inset-x-0 bottom-0 p-4">
                          <Heading
                            size={2}
                            html={matchSeriesMetadata?.banner?.title || 'Buy in Guide'}
                            className={cn('font-bold text-white', {
                              'text-black': matchSeriesMetadata?.banner?.theme === 'dark',
                            })}
                          />
                          <Text
                            html={matchSeriesMetadata?.banner?.desc || '20.000mAh'}
                            className={cn('text-sm font-bold text-white', {
                              'text-black': matchSeriesMetadata?.banner?.theme === 'dark',
                            })}
                          />
                        </div>
                      </div>
                    </Link>
                  )}
                  {seriesItem.products?.map((product: any, index: number) => (
                    <SeriesProductItem
                      seriesLabel={matchSeriesMetadata?.label}
                      key={`seriesProductItem-${seriesItem?.label || ''}-${seriesIndex}-${index}`}
                      product={product}
                      isCollection={matchSeriesMetadata?.isCollection}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    )
  }
)

SidebarDropdown.displayName = 'SidebarDropdown'

/**
 * 系列产品项组件
 */
export const SeriesProductItem = ({
  product,
  isCollection,
  position,
  seriesLabel,
}: {
  product: any
  isCollection?: boolean
  position?: number
  seriesLabel?: string
}) => {
  const { locale = 'us' } = useAiuiContext()
  const { buildProps, onSeriesProductClick } = useNavContext()
  let productData = isCollection
    ? product
    : buildProps?.products?.find((item: Product) => item.handle === product.handle)
  const variant =
    productData?.variants?.find((item: ProductVariant) => item.sku === product.sku) || productData?.variants?.[0]

  const listingLink = useMemo(() => {
    return `${getLocalizedPath(`/products/${productData?.handle}`, locale)}?variant=${atobID(variant?.id as string)}`
  }, [productData?.handle, variant?.id, locale])

  const tags = useMemo(() => {
    return productData?.tags
      ?.filter?.((item: any) => item?.startsWith?.('CLtag'))
      ?.map?.((item: any) => item?.replace?.('CLtag:', ''))
      ?.slice?.(0, 2)
  }, [productData?.tags])

  if (!variant?.availableForSale) return null

  return (
    <Link
      href={listingLink}
      onClick={e => {
        e.preventDefault()
        window.open(listingLink, '_self')
        onSeriesProductClick?.(productData, position || 0, seriesLabel)
      }}
      className="no-underline hover:text-current"
    >
      <div className="rounded-sidebar-shelf flex shrink-0 items-center gap-4 bg-[#F5F5F7] px-4 py-3 [&_img]:hover:scale-110 [&_img]:hover:duration-500">
        <div className="shrink-0">
          <img
            src={`${variant?.image?.url || product?.images?.[0]?.url}&width=200`}
            loading="lazy"
            alt={productData?.title || product?.name}
            width={90}
            height={90}
            className="size-[96px] object-contain"
          />
        </div>
        <div className="relative">
          <div className="flex gap-1">
            {Array.isArray(tags) &&
              tags?.map((tag: string, index: number) => (
                <Text
                  key={index}
                  as="p"
                  html={tag}
                  className="text-brand-0 mb-1 inline-block h-[24px] whitespace-nowrap rounded-full border-[1.6px] border-[#00BEFA] px-[6px] text-xs font-bold !leading-[22px]"
                />
              ))}
          </div>
          <Text
            className="lg-desktop:h-[44px] lg-desktop:text-base line-clamp-2 h-[40px] text-sm font-bold leading-[1.4]"
            html={product?.custom_name || variant?.metafields?.infos?.page_short_title || productData?.title}
          />
          {product?.desc && (
            <Text
              as="p"
              html={product?.desc}
              className="lg-desktop:text-sm mt-1 line-clamp-1 text-xs font-bold leading-[1.4] text-[#6D6D6F]"
            />
          )}
        </div>
      </div>
    </Link>
  )
}
